import { LoadingIndicator } from '#/components/common/loading-state'
/**
 * Sign-in (`/sign-in`) — styled with the marketing vocabulary from `/` (see components/landing/mk.tsx and the
 * `.mk-*` tokens in styles.css) so the page reads as one product with the landing: split layout, dark brand
 * column on the left, plain white form on the right. No video: the panel uses the same CSS wash as `.mk-frame--dark`.
 *
 * Deliberately free of shadcn primitives — `<html>` may carry `.dark` from the app theme, and those components
 * follow it. The landing is light-only under `.mk`, and this page joins it by using raw elements + `.mk-*` classes.
 */
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { toast } from 'sonner'
import { ArrowLeft, Check } from 'lucide-react'
import { Wordmark } from '#/components/layout/wordmark'
import { Mascot } from '#/components/layout/mascot'
import { authClient } from '#/lib/auth-client'
import { m } from '#/paraglide/messages'
import { getLocale } from '#/paraglide/runtime'

const search = z.object({ redirect: z.string().optional() })

export const Route = createFileRoute('/sign-in')({
  validateSearch: search,
  component: SignIn,
})

function GoogleIcon({ className = 'size-[18px]' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path
        fill="#4285F4"
        d="M23.5 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.45a5.5 5.5 0 0 1-2.39 3.61v3h3.86c2.26-2.08 3.56-5.15 3.56-8.8z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.87-3a7.2 7.2 0 0 1-10.72-3.78H1.36v3.09A12 12 0 0 0 12 24z"
      />
      <path
        fill="#FBBC05"
        d="M5.34 14.3a7.2 7.2 0 0 1 0-4.6V6.62H1.36a12 12 0 0 0 0 10.77l3.98-3.09z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.43-3.42C17.95 1.2 15.24 0 12 0A12 12 0 0 0 1.36 6.62l3.98 3.09A7.2 7.2 0 0 1 12 4.75z"
      />
    </svg>
  )
}

/** Static echo of the landing's CLI terminal — the first thing that happens after this sign-in. */
function MiniTerminal() {
  return (
    <div
      className="mk-terminal w-full max-w-md overflow-hidden"
      role="img"
      aria-label={m.mk_term_title()}
    >
      <div className="flex items-center gap-3 border-b border-[var(--mk-dark-border)] px-4 py-2.5">
        <div className="flex gap-1.5" aria-hidden>
          <span className="size-2.5 rounded-full bg-[#ff5f57]" />
          <span className="size-2.5 rounded-full bg-[#febc2e]" />
          <span className="size-2.5 rounded-full bg-[#28c840]" />
        </div>
        <span className="dim text-xs">{m.mk_term_title()}</span>
      </div>
      <div className="px-5 py-4 break-words whitespace-pre-wrap">
        <div className="cmd">
          <span className="dim">$ </span>npx @bystep/cli login
        </div>
        <div className="ok">{m.mk_term_l1()}</div>
        <div className="cmd">
          <span className="dim">$ </span>npx @bystep/cli task next
        </div>
        <div className="arrow">{m.mk_term_l4()}</div>
        <div className="cmd">
          <span className="dim">$ </span>
          <span className="cursor" aria-hidden />
        </div>
      </div>
    </div>
  )
}

/** One proof point. `tone="light"` is the mobile echo of the aside, where the dark column is hidden. */
function Point({
  children,
  tone = 'dark',
}: {
  children: React.ReactNode
  tone?: 'dark' | 'light'
}) {
  const dark = tone === 'dark'
  return (
    <li className="flex gap-3">
      <span
        className={
          dark
            ? 'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md border border-[var(--mk-dark-border)] bg-[var(--mk-dark-card)]'
            : 'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md border border-[var(--mk-border)] bg-[var(--mk-bg-muted)]'
        }
        aria-hidden
      >
        <Check
          className={
            dark ? 'size-3 text-[#8fa8ff]' : 'size-3 text-[var(--mk-accent)]'
          }
        />
      </span>
      <span
        className={
          dark
            ? 'text-[15px] leading-relaxed text-[var(--mk-dark-fg-muted)]'
            : 'text-[14px] leading-relaxed text-[var(--mk-fg-muted)]'
        }
      >
        {children}
      </span>
    </li>
  )
}

/** Landing-flavoured text field: no shadcn, so the app's dark theme can't bleed into this light page. */
function Field({
  id,
  label,
  ...input
}: { id: string; label: string } & React.ComponentProps<'input'>) {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="text-[13px] font-medium text-[var(--mk-fg)]"
      >
        {label}
      </label>
      <input
        id={id}
        {...input}
        className="h-10 w-full rounded-lg border border-[var(--mk-border)] bg-white px-3 text-[15px] text-[var(--mk-fg)] outline-none transition-colors placeholder:text-[var(--mk-fg-faint)] focus:border-[var(--mk-accent)] focus:ring-2 focus:ring-[var(--mk-accent)]/20"
      />
    </div>
  )
}

function SignIn() {
  const { redirect } = Route.useSearch()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const callbackURL = redirect ?? '/home'
  // New accounts (no onboardedAt) go through /onboarding first; it forwards to `redirect` afterwards.
  const onboardingURL = `/onboarding?redirect=${encodeURIComponent(callbackURL)}`
  const devLogin = import.meta.env.VITE_DEV_LOGIN === '1'

  const google = async () => {
    setBusy(true)
    try {
      const result = await authClient.signIn.social({
        provider: 'google',
        callbackURL: onboardingURL,
      })
      if (result.error) { toast.error(result.error.message ?? m.common_error_generic()); setBusy(false) }
    } catch {
      toast.error(m.common_error_generic())
      setBusy(false)
    }
  }

  const dev = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
    const r = await authClient.signIn.email({ email, password })
    if (r.error) {
      const up = await authClient.signUp.email({
        email,
        password,
        name: email.split('@')[0] ?? 'dev',
        locale: getLocale(),
      } as never)
      if (up.error) {
        toast.error(up.error.message ?? m.common_error_generic())
        setBusy(false)
        return
      }
    }
    const s = await authClient.getSession()
    const onboarded = Boolean(
      (s.data?.user as { onboardedAt?: unknown } | undefined)?.onboardedAt,
    )
    await navigate({ to: (onboarded ? callbackURL : onboardingURL) as '/' })
    } catch { toast.error(m.common_error_generic()) }
    finally { setBusy(false) }
  }

  return (
    <main className="mk flex flex-1 overflow-hidden">
      {/* left: brand column, same wash + terminal as the landing's dark sections */}
      <aside className="mk-auth-aside mk-dark-section hidden w-[46%] max-w-[720px] shrink-0 flex-col justify-between gap-12 overflow-y-auto p-12 lg:flex xl:p-16">
        <Wordmark to="/" size="lg" />
        <div className="flex flex-col gap-7">
          <Mascot size={128} eager />
          <p className="mk-overline text-[#93a7ff]!">{m.mk_hero_overline()}</p>
          <h2 className="max-w-[18ch] text-[2.125rem]! leading-[1.18]!">
            {m.mk_hero_title()}
          </h2>
          <ul className="flex max-w-[46ch] flex-col gap-3.5">
            <Point>{m.signin_side_1()}</Point>
            <Point>{m.signin_side_2()}</Point>
            <Point>{m.signin_side_3()}</Point>
          </ul>
          <MiniTerminal />
        </div>
        <p className="text-[13px] text-[var(--mk-dark-fg-muted)]">
          {m.mk_manifesto_quote_org()}
        </p>
      </aside>

      {/* right: the form itself */}
      <div className="flex flex-1 flex-col overflow-y-auto bg-[var(--mk-bg)]">
        <div className="flex items-center justify-between gap-4 px-6 py-5 lg:px-10">
          <Wordmark to="/" className="lg:hidden" />
          <Link
            to="/"
            className="ml-auto inline-flex items-center gap-1.5 rounded-md text-sm text-[var(--mk-fg-muted)] transition-colors hover:text-[var(--mk-fg)]"
          >
            <ArrowLeft className="size-4" aria-hidden />
            {m.signin_back()}
          </Link>
        </div>

        <div className="flex flex-1 items-center justify-center px-6 py-8">
          <div className="w-full max-w-[380px]">
            <h1 className="text-[1.75rem]! leading-tight!">
              {m.signin_title()}
            </h1>
            <p className="mk-muted mt-2 text-[15px] leading-relaxed">
              {m.signin_subtitle()}
            </p>

            <button
              type="button"
              onClick={google}
              disabled={busy} aria-busy={busy}
              className="mk-btn mk-btn-primary mt-8 h-12 w-full disabled:opacity-60"
            >
              <span
                className="flex size-[22px] items-center justify-center rounded-full bg-white"
                aria-hidden
              >
                <GoogleIcon />
              </span>
              {busy ? <LoadingIndicator label={m.loading_session()} className="text-current" /> : m.signin_google()}
            </button>
            <p className="mt-3 text-center text-[13px] text-[var(--mk-fg-faint)]">
              {m.signin_new_account()}
            </p>

            {/* the aside is hidden below lg, so its three points come back here */}
            <ul className="mt-8 flex flex-col gap-3 border-t border-[var(--mk-border)] pt-6 lg:hidden">
              <Point tone="light">{m.signin_side_1()}</Point>
              <Point tone="light">{m.signin_side_2()}</Point>
              <Point tone="light">{m.signin_side_3()}</Point>
            </ul>

            {devLogin ? (
              <form
                onSubmit={dev}
                className="mt-8 space-y-3 rounded-xl border border-dashed border-[var(--mk-border)] bg-[var(--mk-bg-muted)] p-4"
              >
                <p className="text-xs font-medium tracking-[0.02em] text-[var(--mk-fg-muted)] uppercase">
                  {m.signin_dev()}
                </p>
                <Field
                  id="em"
                  label={m.signin_dev_email()}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Field
                  id="pw"
                  label={m.signin_dev_password()}
                  type="password"
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="submit"
                  disabled={busy} aria-busy={busy}
                  className="mk-btn mk-btn-secondary h-10 w-full disabled:opacity-60"
                >
                  {busy ? <LoadingIndicator label={m.loading_session()} className="text-current" /> : m.signin_dev_submit()}
                </button>
              </form>
            ) : null}

            <p className="mt-8 text-center text-[13px] leading-relaxed text-[var(--mk-fg-faint)]">
              {m.signin_terms()}{' '}
              <Link
                className="text-[var(--mk-fg-muted)] underline underline-offset-2 hover:text-[var(--mk-fg)]"
                to="/terms"
              >
                {m.legal_see_terms()}
              </Link>{' '}
              ·{' '}
              <Link
                className="text-[var(--mk-fg-muted)] underline underline-offset-2 hover:text-[var(--mk-fg)]"
                to="/privacy"
              >
                {m.legal_see_privacy()}
              </Link>
            </p>
          </div>
        </div>

        <p className="border-t border-[var(--mk-border)] px-6 py-4 text-center text-[13px] text-[var(--mk-fg-faint)] lg:px-10">
          {m.signin_free_note()}
        </p>
      </div>
    </main>
  )
}
